import { memo, useCallback, type FC } from "react";
import { Stack, Typography } from "@mui/material";
import type { CompanyFilters } from "../types";
import type { FilterModule } from "./index";
import { useCompanyFilterSlice, useCompanyFilterActions } from "../hooks";
import {
  VendorInput,
  type VendorOption,
} from "@/components/input/VendorInput/VendorInput";
import { ConditionSelectFilter } from "@/components/filters/Condition/ConditionSelectFilter";
import { FILTER_MODE } from "@/components/filters/Condition/constants";
import type { FilterMode } from "@/components/filters/Condition/types";
import { InclusionMethod } from "@/generated/company-search";

type BuyerIntentVendorsSlice = {
  vendorIds: number[];
  inclusionMethod: FilterMode;
};
const defaultSlice: BuyerIntentVendorsSlice = {
  vendorIds: [],
  inclusionMethod: FILTER_MODE.ANY,
};

const BuyerIntentVendorsFilter: FC = memo(() => {
  const slice = useCompanyFilterSlice("buyerIntentVendors");
  const { setSlice } = useCompanyFilterActions();

  const handleVendorsSelect = useCallback(
    (vendors: VendorOption[]) => {
      setSlice("buyerIntentVendors", {
        ...slice,
        vendorIds: vendors.map((v) => Number(v.value)),
      });
    },
    [setSlice, slice]
  );

  const handleConditionChange = useCallback(
    (inclusionMethod: FilterMode) => {
      setSlice("buyerIntentVendors", { ...slice, inclusionMethod });
    },
    [setSlice, slice]
  );

  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="center">
        <Typography variant="body2">Include companies researching</Typography>
        <ConditionSelectFilter
          id="buyer-intent-vendor-condition"
          value={slice.inclusionMethod}
          onChange={handleConditionChange}
        />
        <Typography variant="body2">of the following vendors</Typography>
      </Stack>
      <VendorInput
        selectedVendorIds={slice.vendorIds}
        onVendorsSelect={handleVendorsSelect}
      />
    </Stack>
  );
});

BuyerIntentVendorsFilter.displayName = "BuyerIntentVendorsFilter";

export const buyerIntentVendorsModule: FilterModule<BuyerIntentVendorsSlice> = {
  key: "buyerIntentVendors",
  defaultSlice,
  Component: BuyerIntentVendorsFilter,
  toCompanyFilters: (slice) =>
    slice.vendorIds.length > 0
      ? {
          buyerIntentVendors: [
            {
              ids: slice.vendorIds,
              inclusionMethod: slice.inclusionMethod as InclusionMethod,
            },
          ],
        }
      : {},
  fromCompanyFilters: (filters: CompanyFilters) => ({
    vendorIds:
      filters.buyerIntentVendors?.flatMap(
        (v) => v?.ids?.filter((id): id is number => id != null) ?? []
      ) ?? [],
    inclusionMethod:
      (filters.buyerIntentVendors?.[0]?.inclusionMethod as
        | FilterMode
        | undefined) ?? FILTER_MODE.ANY,
  }),
  getChips: (slice, setSlice) =>
    slice.vendorIds.map((id) => ({
      id: `bi-vendor-${id}`,
      label: `BI Vendor: ${id}`,
      filterKey: "buyerIntentVendors" as const,
      onRemove: () =>
        setSlice({
          ...slice,
          vendorIds: slice.vendorIds.filter((x) => x !== id),
        }),
    })),
  getCount: (slice) => slice.vendorIds.length,
};
